import { CoralProductMetaState, ProductMetaState } from '@websrc/Basket/useProductMeta'
import { BuildPagesConfig } from '@websrc/Build/Pages/BuildPagesConfig'
import { StaticTextByLocale, WebsiteSettingsByLocale } from '@websrc/Build/Pages/BuildPagesContent'
import { BuildLocale } from '@websrc/Build/Pages/buildTypes'
import { OtherLocaleUrl } from '@websrc/Build/Pages/getOtherLocaleUrls'
import { mapCookiePolicy } from '@websrc/DataMappers/mapCookiePolicy'
import { mapCoralWebFooter } from '@websrc/DataMappers/mapCoralFooter'
import { mapCoralWebGlobalFooter } from '@websrc/DataMappers/mapCoralGlobalFooter'
import { mapCoralTopbar } from '@websrc/DataMappers/mapCoralTopbar'
import { ExitPopupData } from '@websrc/DataMappers/mapExitPopup'
import { mapFooter } from '@websrc/DataMappers/mapFooter'
import { mapMarkets } from '@websrc/DataMappers/mapMarkets'
import { mapCoralProductPage, mapProductPage } from '@websrc/DataMappers/mapProductPage'
import { mapServicebar } from '@websrc/DataMappers/mapServicebar'
import { mapTopbar, mapTopbarAlerts } from '@websrc/DataMappers/mapTopbar'
import { mapWebsiteSettings } from '@websrc/DataMappers/mapWebsiteSettings'
import { CSBaseType, CSColorTheme, CSLocales, CSStaticText } from '@websrc/Typings/ContentStack'
import { createContext } from 'react'

import { SEOProps } from './Components/SEO'
import { SEOBlogProps } from './Components/SEOBlog'

export interface AppContext {
  locale: BuildLocale
  otherLocaleUrls: OtherLocaleUrl[]
  pageConfigType: BuildPagesConfig['type']
  path: string
  staticText: CSStaticText
  settings: ReturnType<typeof mapWebsiteSettings>
  cookiePolicy: ReturnType<typeof mapCookiePolicy>
  markets: ReturnType<typeof mapMarkets>
  footer?: ReturnType<typeof mapFooter>
  coralFooter?: ReturnType<typeof mapCoralWebFooter>
  coralGlobalFooter?: ReturnType<typeof mapCoralWebGlobalFooter>
  topbar?: ReturnType<typeof mapTopbar>
  topbarAlerts: ReturnType<typeof mapTopbarAlerts>
  coralTopbar?: ReturnType<typeof mapCoralTopbar>
  servicebar?: ReturnType<typeof mapServicebar>
  seo: SEOProps
  seoBlog?: SEOBlogProps
  isMarketingPage?: boolean
  hideTopbarAlerts?: boolean
  hideMainTopbar?: boolean
  showCoralTopbar?: boolean
  showCoralFooter?: boolean
  topBarOnlyShowsLogo?: boolean
  hideFooter?: boolean
  hideCta?: boolean
  disableCookies?: boolean
  adobeLaunchSrc?: string
  pageName?: string
  matomoPageTemplate: string
  matomoProductCategory?: string
  productMetaState: ProductMetaState
  coralProductMetaState: CoralProductMetaState
  exitPopup?: ExitPopupData
  // On product pages this can be set to allow for product view tracking
  productDetails?: ReturnType<typeof mapProductPage | typeof mapCoralProductPage>['product_details']
  siteSection?: string
  pageType: string
  settingsByLocale?: WebsiteSettingsByLocale
  staticTextByLocale?: StaticTextByLocale
  colorTheme?: CSColorTheme['select_theme']
  // Set to false if you want to call trackPageView yourself
  matomoCallTrackPageView?: boolean
}

export interface PageContext {
  appContext: AppContext
}

interface AppProviderContextProps {
  locale: CSLocales
  path: string
  staticText: Omit<CSStaticText, keyof CSBaseType> | null
  settings: ReturnType<typeof mapWebsiteSettings> | null
  pageConfigType: BuildPagesConfig['type']
  pageType: string
  matomoPageTemplate: string
  settingsByLocale?: {
    [key: string]: ReturnType<typeof mapWebsiteSettings>
  }
  staticTextByLocale?: {
    [key: string]: CSStaticText
  }
  disableCookies?: boolean
}

const initial: AppProviderContextProps = {
  locale: 'en-us',
  path: '/',
  staticText: null,
  settings: null,
  pageConfigType: 'dot-com',
  pageType: 'unknown',
  matomoPageTemplate: 'unknown',
}

export const AppProviderContext = createContext<AppProviderContextProps>(initial)
